// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyBulkResultDetails = {
    /** What landed. Zero is a real answer and gets its own line. */
    done: number;
    /** What did not — skipped by a rule, or every record when the write was refused outright. */
    failed: number;
    /** The verb in the past tense, from the section's own words: changed, deleted, restored. */
    doneLabel?: string;
    /** The server's refusal, when there was one. */
    error?: string | null;
};

type Props = { onClose: () => void; details?: unknown };

/**
 * What the bulk write actually did, said out loud.
 *
 * A bulk action is the one write in this app whose answer is **two numbers rather than one**: it can
 * land on thirty-seven of forty, and the three it did not reach are the three somebody has to go and
 * look at. Before this, that arrived as a small line under the list — which is where a person who
 * has just pressed a button is not looking.
 *
 * Both numbers are always shown, including zero. "37 changed" alone invites the reader to assume the
 * other three also went; "37 changed · 3 unchanged" does not.
 */
export default function BulkResultPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { done, failed, doneLabel, error } = details as TyBulkResultDetails;

    return (
        <section className="popup-card bulk-result">
            <h2 className="bulk-result__title">{done ? t.bulk.resultTitle : t.bulk.nothingChanged}</h2>

            <div className="bulk-result__tallies">
                <span className="bulk-result__tally bulk-result__tally--done">
                    <Icon name="check" size={15} />
                    {(doneLabel ?? t.bulk.changed).replace("{count}", String(done))}
                </span>

                {failed > 0 && (
                    <span className="bulk-result__tally bulk-result__tally--failed">
                        <Icon name="close" size={13} />
                        {t.bulk.failed.replace("{count}", String(failed))}
                    </span>
                )}
            </div>

            {/* Why, where the server said why. Otherwise the standing reason a row gets skipped. */}
            {failed > 0 && <p className="bulk-result__why">{error ?? t.bulk.andFailed}</p>}

            <button type="button" className="bulk-result__close" onClick={onClose}>
                {t.common.done}
            </button>
        </section>
    );
}
